import { PhoneIcon, SearchIcon } from "@heroicons/react/outline";
import { Modal, Stepper } from "@tyrannosaurustech/ui";
import { useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import Async from "react-select/async";
import {
  useSearchProvidersByName,
  useUpdateProviderConnectionMutation,
  useBulkCreateConnectionsMutation,
} from "../../redux/reduxQuery/medicalProviderApi";
import { BadgeStatus } from "../atoms/Badge";
import { CustomSheetButton } from "../atoms/Button";
import { Theme } from "../atoms/theme";
import { ParagraphS, SubtitlesS } from "../atoms/Typography";
import ConnectionDetailForm from "./ConnectionDetailForm";
import useToast from "../../hooks/useToast";

interface CreateMedicalProviderConnectionModalProps {
  setOpen?: (value: boolean) => void;
  open?: boolean;
  provider?: any;
  editMode?: boolean;
  connectedProvider?: any;
  refetchFunction?: any;
  setEditMode?: (value: boolean) => void;
  form: UseFormReturn<FieldValues, any>;
}

export const CreateMedicalProviderConnectionModal = ({
  setOpen = () => {},
  open = false,
  provider = {},
  editMode = false,
  connectedProvider = null,
  refetchFunction = () => {},
  setEditMode = () => {},
  form,
}: CreateMedicalProviderConnectionModalProps) => {
  const searchProviders = useSearchProvidersByName();
  const [selectedProviders, setSelectedProviders] = useState<any[]>(
    connectedProvider ? [connectedProvider] : []
  );
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const numberOfSteps = editMode ? 1 : 2;
  const isLastStep = step === numberOfSteps - 1;
  const [updateProviderConnection] = useUpdateProviderConnectionMutation();
  const [bulkCreateConnections] = useBulkCreateConnectionsMutation();
  const toast = useToast();

  const isBulkMode = !editMode && selectedProviders.length > 1;

  const handleClose = () => {
    setEditMode(false);
    setSelectedProviders([]);
    setOpen(false);
    setStep(0);
  };

  const onSubmit = form.handleSubmit(async (data) => {
    setIsSubmitting(true);
    try {
      if (editMode) {
        // Edit mode: update single connection
        await updateProviderConnection({
          toId: connectedProvider?.id,
          fromId: provider.id,
          validFrom: data.validFrom,
          validTo: data.validTo,
          requestType: data.requestType?.id,
          connectionNotes: data.connectionNotes,
        });
      } else if (selectedProviders.length === 1) {
        // Single provider selected: use existing endpoint
        await updateProviderConnection({
          toId: selectedProviders[0]?.id,
          fromId: provider.id,
          validFrom: data.validFrom,
          validTo: data.validTo,
          requestType: data.requestType?.id,
          connectionNotes: data.connectionNotes,
        });
      } else {
        // Multiple providers selected: use bulk endpoint
        const result = await bulkCreateConnections({
          fromId: provider.id,
          toIds: selectedProviders.map((p) => p.id),
          validFrom: data.validFrom,
          validTo: data.validTo,
          requestType: data.requestType?.id,
          connectionNotes: data.connectionNotes,
        }).unwrap();

        if (result.failed.length > 0) {
          toast.warn(
            `Added ${result.succeeded.length} connections, ${result.failed.length} failed`
          );
        } else {
          toast.success(`Added ${result.succeeded.length} connections`);
        }
      }
      refetchFunction();
      handleClose();
    } catch (error) {
      toast.error("Failed to create connections");
    } finally {
      setIsSubmitting(false);
    }
  });

  const getTitle = () => {
    if (isLastStep) {
      return isBulkMode
        ? `Connection Details (${selectedProviders.length} providers)`
        : "Relevant for this Connection";
    }
    return "Select Medical Providers";
  };

  const getConfirmText = () => {
    if (isLastStep) {
      if (editMode) return "Save";
      if (isBulkMode) return `Add ${selectedProviders.length} Connections`;
      return "Finish";
    }
    return "Next";
  };

  return (
    <Modal
      open={open}
      setOpen={(shouldOpen) => {
        if (!shouldOpen) {
          handleClose();
        }
      }}
      loading={isSubmitting}
      title={getTitle()}
      confirmText={getConfirmText()}
      cancelText="Cancel"
      onConfirm={() => {
        isLastStep ? onSubmit() : setStep(step + 1);
      }}
      noConfirm={selectedProviders.length === 0 && !editMode}
      panelClassName="overflow-visible"
    >
      <div style={{ width: "100%" }}>
        {!editMode && (
          <Stepper
            containerClasses="w-full mt-1 mb-4 p-2"
            currentStep={step}
            successStepColor="bg-primary"
            numberOfSteps={numberOfSteps}
            pendingStepLineClasses="h-[0.15rem]"
            successLineClasses="h-[0.15rem] bg-primary"
          />
        )}
        {!editMode && step === 0 && (
          <SelectedProviderScreen
            provider={provider}
            searchProviders={searchProviders}
            setSelectedProviders={setSelectedProviders}
            selectedProviders={selectedProviders}
          />
        )}
        {(editMode || step === 1) && (
          <ConnectionDetailsScreen form={form} isBulkMode={isBulkMode} />
        )}
      </div>
    </Modal>
  );
};

const ConnectionDetailsScreen = ({
  form,
  isBulkMode = false,
}: {
  form: any;
  isBulkMode?: boolean;
}) => {
  return (
    <div>
      <ParagraphS
        color={Theme.colors.gray_200}
        style={{ marginBottom: "1rem" }}
      >
        {isBulkMode
          ? "These details will be applied to all selected connections. You can edit individual connections later."
          : "Please list the dates when this connection is relevant to this provider, along with the corresponding request type that makes it relevant. Feel free to add any additional notes with extra information if needed."}
      </ParagraphS>
      <ConnectionDetailForm {...form} />
    </div>
  );
};

const SelectedProviderScreen = ({
  provider,
  searchProviders,
  setSelectedProviders,
  selectedProviders,
}: {
  provider: any;
  searchProviders: (name: string) => Promise<any[]>;
  setSelectedProviders: (providers: any[]) => void;
  selectedProviders: any[];
}) => {
  // Filter out the current provider from search results to prevent self-connections
  const filteredSearchProviders = async (name: string) => {
    const results = await searchProviders(name);
    return results.filter((p: any) => p.id !== provider.id);
  };

  return (
    <div>
      <ParagraphS
        color={Theme.colors.gray_200}
        style={{ marginBottom: "1rem" }}
      >
        {`Select one or more medical providers to connect to ${provider.name}.
        You can select multiple providers to add them all at once.`}
      </ParagraphS>
      <Async
        isMulti
        loadOptions={filteredSearchProviders}
        getOptionLabel={(p: any) => p.name}
        getOptionValue={(p: any) => p.id}
        onChange={(options) => {
          setSelectedProviders(options ? [...options] : []);
        }}
        value={selectedProviders}
        isClearable
        cacheOptions
        placeholder="Search and select providers..."
        closeMenuOnSelect={false}
        components={{
          Option: ProviderOption,
          NoOptionsMessage: () => {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0,
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
              >
                <div
                  style={{
                    borderRadius: "50%",
                    backgroundColor: Theme.colors.neutrals_400,
                    padding: "0.5rem",
                  }}
                >
                  <SearchIcon
                    width={"1.5rem"}
                    color={Theme.colors.neutrals_000}
                  />
                </div>

                <span style={{ color: Theme.colors.neutrals_000 }}>
                  Search for medical providers
                </span>

                <span>Try searching by address, phone, or name</span>
              </div>
            );
          },
        }}
        styles={{
          container: (provided) => ({
            ...provided,
            width: "100%",
          }),
          control: (provided) => ({
            ...provided,
            minHeight: "44px",
            marginBottom: "1rem",
            width: "100%",
          }),
          multiValue: (provided) => ({
            ...provided,
            backgroundColor: Theme.colors.primaries_300,
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: Theme.colors.primaries_000,
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: Theme.colors.primaries_000,
            ":hover": {
              backgroundColor: Theme.colors.primaries_neg100,
              color: "white",
            },
          }),
        }}
      />
      {selectedProviders.length > 0 && (
        <ParagraphS color={Theme.colors.primaries_000}>
          {selectedProviders.length} provider
          {selectedProviders.length > 1 ? "s" : ""} selected
        </ParagraphS>
      )}
    </div>
  );
};

const MedicalProviderOptions = ({ provider }) => {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div style={{ textAlign: "left" }}>
        <SubtitlesS>{provider.name}</SubtitlesS>
        <ParagraphS>{provider.address}</ParagraphS>
        <ParagraphS color={Theme.colors.gray_200}>{provider.type}</ParagraphS>
      </div>
      <div style={{ alignContent: "start" }}>
        <PhoneIcon color={Theme.colors.primaries_000} width={"1.25rem"} />
      </div>
    </div>
  );
};
const ProviderOption = ({ data, label, innerProps }) => {
  return (
    <CustomSheetButton
      width={"100%"}
      icon={false}
      color={"#AE3EC9"}
      badge={<BadgeStatus status={data.status} />}
      buttonText={label}
      onClick={() => innerProps.onClick()}
      children={<MedicalProviderOptions provider={data} />}
      style={{ justifyContent: "space-between" }}
    />
  );
};
